import _ from "lodash";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";

import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Label,
  ResponsiveContainer,
  Bar,
  ComposedChart,
  LabelList,
  Brush,
} from "recharts";
import { Card, Table, TableBody, TableRow, TableCell } from "@mui/material";
import { IconPennant2, IconRun } from "@tabler/icons-react";

import ImageTile from "features/photos/ImageTile";
import Image from "data/firestore/image";
import AnalyticsService from "data/firestore/analytics";
import TimeUtils from "utils/time";

const stroke = "#8bc34a";
const barColor = "#90caf9";

const CustomizedDot = ({ cx, cy, value, payload, images }) => {
  const dateString = moment(payload.x, "MMM YYYY").format(
    TimeUtils.FullDateString
  );
  const isFavorited = _.some(images, (img) => img.month === dateString);
  if (isFavorited) {
    return <IconPennant2 x={cx - 12.5} y={cy - 17} width={20} height={20} />;
  }

  if (payload.isLastDataPoint) {
    return <IconRun x={cx - 10} y={cy - 10} width={20} height={20} />;
  }

  if (value > 0) {
    return (
      <circle
        cx={cx}
        cy={cy}
        r={2}
        stroke={stroke}
        strokeWidth={1}
        fill={stroke}
      />
    );
  }
  return null;
};

const MilestoneMarker = ({ x, y, width, data, index, images, aggregated }) => {
  if (aggregated) {
    return null;
  }
  const date = moment(data[index].x, "MMM YYYY");
  const isFavorited = _.some(images, (img) => img.sameMonthAndYear(date));
  const isLastDataPoint = data[index].isLastDataPoint;

  if (isFavorited) {
    return (
      <g>
        <IconPennant2
          x={x + width / 2 - 12} // Centered on the bar
          y={y - 18} // Just above the bar top
          width={20}
          height={20}
        />
      </g>
    );
  }

  if (isLastDataPoint) {
    return <IconRun x={x + width / 2 - 10} y={y - 18} width={20} height={20} />;
  }

  return null;
};

const CustomTooltip = ({
  active,
  payload,
  label,
  images,
  showAllImages,
  viewedUserAbove18,
  currentUserAbove18,
}) => {
  const date = moment(label, "MMM YYYY");
  const image = _.first(_.filter(images, (img) => img.sameMonthAndYear(date)));

  let imageContent;
  if (image) {
    // Only show images if both viewed user and current user are above 18
    const canShowImage = viewedUserAbove18 && currentUserAbove18;
    imageContent = (
      <ImageTile
        image={image}
        editable={false}
        onGraph={true}
        userAbove18={canShowImage}
      />
    );
  }

  if (active && payload && payload.length) {
    const analyticsData = { tooltipHasImage: !!imageContent };
    if (!!imageContent) {
      analyticsData.imageShown = showAllImages;
    }
    AnalyticsService.throttleChartHover(
      "hover_on_timeline",
      analyticsData,
      label
    );

    const total = _.get(payload[0], ["payload", "aggregate", payload[0].name]);
    return (
      <Card
        style={{
          backgroundColor: "rgba(0, 0, 0, 0.85)",
          borderRadius: 24,
        }}
      >
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell
                align="right"
                style={{
                  fontWeight: "bold",
                  borderBottom: "none",
                  color: "#F9F6EE",
                }}
              >
                {label}
              </TableCell>
              <TableCell
                align="right"
                style={{ borderBottom: "none", color: "white" }}
              >
                {payload[0].value.toLocaleString()}
              </TableCell>
            </TableRow>

            {total > 0 && (
              <TableRow>
                <TableCell
                  align="right"
                  style={{
                    fontWeight: "bold",
                    borderBottom: "none",
                    color: "#F9F6EE",
                  }}
                >
                  Total
                </TableCell>
                <TableCell
                  align="right"
                  style={{ borderBottom: "none", color: "white" }}
                >
                  {total}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {imageContent}
      </Card>
    );
  }

  return null;
};

const SmoothLineChart = ({
  uid,
  data,
  metric,
  selectedAlbum,
  aggregated,
  setBrushRange,
  showAllImages = false,
  brushRange,
  viewedUserAbove18 = false,
  currentUserAbove18 = false,
}) => {
  const [allImages, setAllImages] = useState([]);
  const [images, setImages] = useState([]);

  // Filter down to first month of the year only
  const ticks = useMemo(() => {
    let filteredData = data;

    // Filter data based on brush range if provided
    if (brushRange && brushRange.length === 2) {
      const [startIndex, endIndex] = brushRange;
      filteredData = data.slice(startIndex, endIndex + 1);
    }

    return _.filter(filteredData, (d) => _.includes(d.x, "Jan")).map(
      (d) => d.x
    );
  }, [data, brushRange]);

  useEffect(() => {
    Image.list(uid).then((list) => {
      const favorited = _.filter(list, (img) => img.favorited);
      setImages(favorited);
      setAllImages(favorited);
    });
  }, [uid]);

  useEffect(() => {
    if (selectedAlbum) {
      setImages(_.filter(allImages, (img) => img.album === selectedAlbum));
    } else {
      setImages(allImages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAlbum]);

  const chartData = useMemo(() => {
    let filteredData = data;

    // Filter data based on brush range if provided
    if (brushRange && brushRange.length === 2) {
      const [startIndex, endIndex] = brushRange;
      filteredData = data.slice(startIndex, endIndex + 1);
    }

    return filteredData.map((entry, index) => ({
      ...entry,
      isLastDataPoint: index === filteredData.length - 1,
    }));
  }, [data, brushRange]);

  function handleBrushChange(brushData) {
    if (
      brushData &&
      brushData.startIndex !== undefined &&
      brushData.endIndex !== undefined
    ) {
      setBrushRange([brushData.startIndex, brushData.endIndex]);
    } else {
      setBrushRange(null);
    }
  }

  return (
    <>
      <ResponsiveContainer width="100%" height={370}>
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="x"
            ticks={ticks}
            tickFormatter={(t) => _.slice(t, -4).join("")}
            padding={{ left: 20, right: 10 }}
          />
          <YAxis allowDecimals={false}>
            <Label
              value={_.capitalize(metric)}
              offset={5}
              position="insideLeft"
              angle={-90}
              style={{ textAnchor: "middle" }}
            />
          </YAxis>
          <Tooltip
            content={
              <CustomTooltip
                images={images}
                showAllImages={showAllImages}
                viewedUserAbove18={viewedUserAbove18}
                currentUserAbove18={currentUserAbove18}
              />
            }
            wrapperStyle={{ zIndex: 1 }}
          />

          {/* Monthly bars (e.g., sessions or hours per month) */}
          <Bar
            dataKey={metric}
            fill={barColor}
            barSize={20}
            radius={[4, 4, 0, 0]}
            minPointSize={2}
          >
            <LabelList
              content={
                <MilestoneMarker
                  data={chartData}
                  images={images}
                  aggregated={aggregated}
                />
              }
            />
          </Bar>

          {/* Aggregated smooth line */}
          <Line
            dataKey={(entry) => entry.aggregate?.[metric]}
            name={`Total ${_.capitalize(metric)}`}
            type="monotone"
            stroke={stroke}
            strokeWidth={5}
            dot={aggregated ? <CustomizedDot images={images} /> : null}
          />
        </ComposedChart>
      </ResponsiveContainer>

      <ResponsiveContainer width="100%" height={35}>
        <ComposedChart
          width={500}
          height={35}
          data={data}
          margin={{
            top: 0,
            right: 15,
            left: 55,
            bottom: 5,
          }}
        >
          <Brush
            stroke="#b0bec5"
            height={30}
            dataKey="x"
            onChange={handleBrushChange}
            startIndex={brushRange ? brushRange[0] : 0}
            endIndex={brushRange ? brushRange[1] : data.length - 1}
            travellerWidth={10}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </>
  );
};

export default SmoothLineChart;
